import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {getSafely} from 'shared/web';

const HEX_COLOR_PATTERN = /^#([0-9a-f]{6})$/i;
const HEX_RADIX = 16;

function parseHexColor(color: string): [number, number, number] | null {
  const matched = HEX_COLOR_PATTERN.exec(color)?.[1];
  if (isNil(matched)) {
    return null;
  }
  return [
    parseInt(matched.slice(0, 2), HEX_RADIX),
    parseInt(matched.slice(2, 4), HEX_RADIX),
    parseInt(matched.slice(4, 6), HEX_RADIX),
  ];
}

/**
 * Spreads the given colour stops into `count` evenly interpolated colours — the
 * first output equals the first stop, the last equals the last stop, and everything
 * between blends linearly in RGB. Used to tint N price bands with the same
 * green→red ramp the five quintile zones use.
 */
export function interpolatePaletteColors(stops: string[], count: number): string[] {
  return match({stopCount: stops.length, count})
    .when(
      (input) => input.count <= 0 || input.stopCount === 0,
      () => []
    )
    .when(
      (input) => input.stopCount === 1 || input.count === 1,
      (input) => Array.from({length: input.count}, () => stops[0] ?? '')
    )
    .otherwise((input) => {
      // Stops are parsed once up front; the per-colour loop only blends numbers.
      const parsedStops = stops.map(parseHexColor);
      return Array.from({length: input.count}, (_, index) => {
        const position = (index / (input.count - 1)) * (input.stopCount - 1);
        const lowIndex = Math.min(Math.floor(position), input.stopCount - 2);
        const fraction = position - lowIndex;
        const fromColor = getSafely(parsedStops, lowIndex);
        const toColor = getSafely(parsedStops, lowIndex + 1);
        if (isNil(fromColor) || isNil(toColor)) {
          return getSafely(stops, lowIndex) ?? stops[0] ?? '';
        }
        const [fromRed, fromGreen, fromBlue] = fromColor;
        const [toRed, toGreen, toBlue] = toColor;
        const blendChannel = (from: number, to: number): number =>
          Math.round(from + (to - from) * fraction);
        return `rgb(${blendChannel(fromRed, toRed)}, ${blendChannel(fromGreen, toGreen)}, ${blendChannel(fromBlue, toBlue)})`;
      });
    });
}
